import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import './Pages.css';

const PractitionerInvoices = ({ user, apiUrl, token }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [remindingId, setRemindingId] = useState(null);
  const [remindResult, setRemindResult] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  const userRole = user?.role || JSON.parse(localStorage.getItem('user') || '{}')?.role;
  const userId = user?.id || JSON.parse(localStorage.getItem('user') || '{}')?.id;

  useEffect(() => {
    if (userRole !== 'practitioner' && userRole !== 'admin') {
      navigate('/');
      return;
    }
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, navigate]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/practitioners/${userId}/invoices`, { headers });
      setInvoices(response.data || []);
    } catch (err) {
      setError('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const remind = async (invoiceId) => {
    setRemindingId(invoiceId);
    setRemindResult((prev) => ({ ...prev, [invoiceId]: null }));
    try {
      const response = await axios.post(`${apiUrl}/practitioners/${userId}/invoices/${invoiceId}/remind`, {}, { headers });
      setRemindResult((prev) => ({
        ...prev,
        [invoiceId]: response.data.sent ? 'Reminder sent' : 'Connect Google (Gmail) in your dashboard to send reminders'
      }));
    } catch (err) {
      setRemindResult((prev) => ({ ...prev, [invoiceId]: err.response?.data?.error || 'Failed to send reminder' }));
    } finally {
      setRemindingId(null);
    }
  };

  const total = invoices.reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0).toFixed(2);
  const unpaid = invoices
    .filter((inv) => inv.status === 'unpaid')
    .reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0)
    .toFixed(2);

  if (loading) return <div className="page-container">Loading invoices...</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Invoices</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/practitioner/dashboard')}>Back to Dashboard</button>
      </div>
      {error && <div className="error-message">{error}</div>}

      {invoices.length > 0 && (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginBottom: 24 }}>
          <div className="stat-tile">
            <div className="stat-value">${total}</div>
            <div className="stat-label">Total Invoiced</div>
          </div>
          <div className="stat-tile">
            <div className="stat-value">${unpaid}</div>
            <div className="stat-label">Outstanding</div>
          </div>
        </div>
      )}

      <div className="list-container table-wrap">
        {invoices.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Client</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>{invoice.service_name}</td>
                  <td>{invoice.client_name}</td>
                  <td>${parseFloat(invoice.amount).toFixed(2)}</td>
                  <td><span className={`badge badge-${invoice.status}`}>{invoice.status}</span></td>
                  <td>{new Date(invoice.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Link to={`/bookings/${invoice.booking_id}`} className="btn btn-secondary btn-sm">View</Link>
                      {invoice.status === 'unpaid' && (
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={remindingId === invoice.id}
                          onClick={() => remind(invoice.id)}
                        >
                          {remindingId === invoice.id ? 'Sending...' : 'Resend Reminder'}
                        </button>
                      )}
                    </div>
                    {remindResult[invoice.id] && (
                      <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: 4, textAlign: 'right' }}>
                        {remindResult[invoice.id]}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="empty-state">No invoices yet.</p>
        )}
      </div>
    </div>
  );
};

export default PractitionerInvoices;
